/**
 * PromoCodeInput
 *
 * Input component for entering and validating promotional codes.
 * Shows discount preview when a valid code is applied.
 */

import { useState } from 'react';
import { Tag, Check, X, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { handleError } from '../../utils/errorHandler';
import { buildCloudApiUrl } from '../../services/urlResolver';
import { getAuthHeaders } from '../../utils/httpUtils';

// ============================================================================
// Types
// ============================================================================

export interface DiscountPreview {
  code: string;
  type: string;
  original_price: number;
  discount_amount: number;
  final_price: number;
}

interface PromoCodeInputProps {
  packageId?: string;
  purchaseAmount?: number;
  onApply: (discount: DiscountPreview | null, code: string | null) => void;
  disabled?: boolean;
}

type ValidationStatus = 'idle' | 'loading' | 'valid' | 'invalid';

// ============================================================================
// Component
// ============================================================================

export function PromoCodeInput({
  packageId,
  purchaseAmount,
  onApply,
  disabled = false,
}: PromoCodeInputProps) {
  const { t } = useTranslation();
  const { getIdToken } = useAuth();
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<ValidationStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [discount, setDiscount] = useState<DiscountPreview | null>(null);

  // /api/v1/promo/* is Cloud-Run-only — must use buildCloudApiUrl so it works
  // in Electron desktop too (getApiBaseUrl would hit localhost -> 404).
  const validateCode = async () => {
    const trimmedCode = code.trim();
    if (!trimmedCode) return;

    setStatus('loading');
    setError(null);

    try {
      const token = await getIdToken();
      if (!token) {
        throw new Error(t('promo.authRequired', 'Authentication required'));
      }

      const response = await fetch(buildCloudApiUrl('/api/v1/promo/validate'), {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          code: trimmedCode,
          package_id: packageId,
          purchase_amount: purchaseAmount,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setStatus('valid');
        const discountData: DiscountPreview = {
          code: result.data.code,
          type: result.data.type,
          original_price: result.data.discount_preview?.original_price || purchaseAmount || 0,
          discount_amount: result.data.discount_preview?.discount_amount || 0,
          final_price: result.data.discount_preview?.final_price || purchaseAmount || 0,
        };
        setDiscount(discountData);
        onApply(discountData, result.data.code);
      } else {
        setStatus('invalid');
        setError(result.message || t('promo.invalidCode', 'Invalid promo code'));
        onApply(null, null);
      }
    } catch (err) {
      setStatus('invalid');
      setError(handleError(err, 'PromoCode', t('promo.validationError', 'Error validating code')));
      onApply(null, null);
    }
  };

  const clearCode = () => {
    setCode('');
    setStatus('idle');
    setError(null);
    setDiscount(null);
    onApply(null, null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && code.trim() && status !== 'loading' && status !== 'valid') {
      e.preventDefault();
      validateCode();
    }
  };

  return (
    <div className="space-y-2">
      <label className="label-themed-bare">{t('promo.label', 'Promo code')}</label>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyDown={handleKeyDown}
            placeholder={t('promo.placeholder', 'Enter code')}
            className="w-full pl-10 pr-10 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-700 text-secondary-900 dark:text-white placeholder-secondary-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={status === 'valid' || disabled}
            maxLength={30}
          />
          {status === 'valid' && (
            <button
              onClick={clearCode}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-secondary-100 dark:hover:bg-secondary-600 rounded transition-colors"
              aria-label={t('promo.clear', 'Clear code')}
            >
              <X className="h-4 w-4 text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-300" />
            </button>
          )}
        </div>

        <button
          onClick={validateCode}
          disabled={!code.trim() || status === 'loading' || status === 'valid' || disabled}
          className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] min-w-[80px] flex items-center justify-center"
        >
          {status === 'loading' ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : status === 'valid' ? (
            <Check className="h-5 w-5" />
          ) : (
            t('promo.apply', 'Apply')
          )}
        </button>
      </div>

      {/* Success message */}
      {status === 'valid' && discount && discount.discount_amount > 0 && (
        <div className="flex items-center gap-2 text-green-600 dark:text-green-400 text-sm">
          <Check className="h-4 w-4 flex-shrink-0" />
          <span>
            {t('promo.discountApplied', 'Discount of {{amount}} tokens applied!', {
              amount: Math.round(discount.discount_amount * 1000).toLocaleString(),
            })}
          </span>
        </div>
      )}

      {/* Token grant codes show different message */}
      {status === 'valid' &&
        discount &&
        discount.discount_amount === 0 &&
        discount.type === 'tokens_fixed' && (
          <div className="flex items-center gap-2 text-green-600 dark:text-green-400 text-sm">
            <Check className="h-4 w-4 flex-shrink-0" />
            <span>{t('promo.codeValid', 'Code valid! Tokens will be added after purchase.')}</span>
          </div>
        )}

      {/* Error message */}
      {status === 'invalid' && error && (
        <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm">
          <X className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

export default PromoCodeInput;
